/**
 * CardCarousel - Image carousel for listing cards
 *
 * Diamond Architecture: Matches Rails CardCarouselComponent
 * - Touch-swipable on mobile
 * - Hover arrows on desktop
 * - Lazy loads non-first images
 * - Shows dot indicators
 *
 * PR-B2: the swipe/arrow/dot mechanic moved to the shared
 * `SlideCarousel` (also driving the estate index-card carousel);
 * this component is now just its S3-listing-photo specialization.
 *
 * Unicorn Standard: Entity Component (src/components/listings)
 * Used by: ListingCard, SelectedListingCard
 */
import { SlideCarousel } from '@/shared/components/listings/SlideCarousel';
import { plainText, type LocalizedText, type S3Key } from '@letsgethome/types/branded';
import { S3Image } from '@letsgethome/ui/s3-image';

// ----------------------------------------------------------------------------
// Types
// ----------------------------------------------------------------------------

interface CardCarouselProps {
  /** Array of S3 image keys */
  images: S3Key[];
  /** Alt text for accessibility */
  alt: LocalizedText | '';
  /** Additional classes for container */
  className?: string;
  /** Fallback when no images */
  fallback?: React.ReactNode;
  /** Responsive sizes attribute for next/image srcset selection */
  sizes: string;
  /**
   * Mark this card as an above-the-fold LCP candidate: only then does the first
   * slide preload (`<link rel=preload fetchpriority=high>` + eager). Default
   * false — a grid of cards must NOT each preload their first image, or the
   * dozens of competing high-priority preloads invert the real LCP's priority
   * (every card "high priority" means none is). Opt in for the single LCP card.
   */
  priority?: boolean;
}

// ----------------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------------

export function CardCarousel({
  images,
  alt,
  className,
  fallback,
  sizes,
  priority = false,
}: CardCarouselProps) {
  return (
    <SlideCarousel
      className={className}
      fallback={fallback}
      slides={images.map((key, index) => ({
        key,
        content: (
          <S3Image
            src={key}
            alt={plainText(`${alt} - ${index + 1}`)}
            fill
            preload={priority && index === 0}
            className="object-cover"
            sizes={sizes}
          />
        ),
      }))}
    />
  );
}
